import { IonContent, IonPage, IonSkeletonText } from '@ionic/react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useCompany, useModuleLevel } from '~/api/hooks/useCompany';
import { useInvoiceSummaryQuery, useInvoicesQuery } from '~/api/hooks/useInvoices';
import type { InvoiceListItem, InvoiceStatus } from '~/api/invoices';
import { CompanySwitcher } from '~/components/CompanySwitcher';
import { InstallPrompt } from '~/components/InstallPrompt';
import { IosInstallSheetAuto } from '~/components/IosInstallSheet';
import { Avatar, initialsFrom } from '~/components/ui/Avatar';
import { GridPattern } from '~/components/ui/GridPattern';
import { Icon } from '~/components/ui/Icon';
import { SectionHead } from '~/components/ui/SectionHead';
import { Stat } from '~/components/ui/Stat';
import { capitalize, splitBrl } from '~/lib/format';
import { visibleShortcuts } from '~/pages/dashboard/shortcuts';
import { ROUTES, notaPath } from '~/routes';
import { ACCESS_LEVEL, atLeast } from '~/session/access';
import { useAuthStore } from '~/stores/auth';

const STATUS_TONE: Record<InvoiceStatus, { dotColor: string; tagClass: string }> = {
  AUTHORIZED: { dotColor: 'var(--rx-positive)', tagClass: 'rx-tag-pos' },
  PROCESSING: { dotColor: 'var(--rx-warn)', tagClass: 'rx-tag-warn' },
  DRAFT: { dotColor: 'var(--rx-text-3)', tagClass: 'rx-tag' },
  ERROR: { dotColor: 'var(--rx-negative)', tagClass: 'rx-tag-neg' },
  REJECTED: { dotColor: 'var(--rx-negative)', tagClass: 'rx-tag-neg' },
  CANCELLED: { dotColor: 'var(--rx-text-2)', tagClass: 'rx-tag' },
};

function greetingForHour(hour: number): string {
  if (hour >= 5 && hour < 12) return 'Bom dia';
  if (hour >= 12 && hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

function firstNameOf(value: string): string {
  const cleaned = value.trim();
  if (!cleaned) return '';
  return cleaned.split(/\s+/)[0] ?? '';
}

const TODAY_OVERLINE = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'long',
  day: '2-digit',
  month: 'short',
});

export function Dashboard(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const { selected: company } = useCompany();
  const nfseLevel = useModuleLevel('nfse');
  const canReadInvoices = atLeast(nfseLevel, ACCESS_LEVEL.read);
  const canIssue = atLeast(nfseLevel, ACCESS_LEVEL.full);
  const shortcuts = visibleShortcuts(company);

  const firstName = useMemo(
    () => firstNameOf(user?.full_name || user?.username || ''),
    [user?.full_name, user?.username],
  );
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);
  const todayLabel = useMemo(() => {
    const parts = TODAY_OVERLINE.formatToParts(new Date());
    const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    return `${capitalize(weekday.replace('-feira', ''))} · ${day} ${month}`;
  }, []);

  const summaryQuery = useInvoiceSummaryQuery(company?.id, { enabled: canReadInvoices });
  const recentQuery = useInvoicesQuery(company?.id, { limit: 5 }, { enabled: canReadInvoices });

  const initials = initialsFrom(user?.full_name || user?.username || 'Cliente');

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen" style={{ paddingBottom: 28, minHeight: '100%' }}>
          <div style={{ padding: '12px 24px 0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--rx-text-3)',
                    fontWeight: 500,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                  }}
                >
                  {todayLabel}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 22,
                    fontWeight: 500,
                    color: 'var(--rx-text)',
                    letterSpacing: '-0.4px',
                  }}
                >
                  {firstName ? `${greeting}, ${firstName}` : greeting}
                </div>
                {company && (
                  <div style={{ marginTop: 8 }}>
                    <CompanySwitcher compact />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                <Link to={ROUTES.notas} className="rx-iconbtn" aria-label="Notificações">
                  <Icon name="bell" size={18} />
                </Link>
                <Link to={ROUTES.perfil} aria-label="Perfil" style={{ display: 'block' }}>
                  <Avatar initials={initials} size={38} />
                </Link>
              </div>
            </div>
          </div>

          {!canReadInvoices && <NoInvoiceAccess hasCompany={Boolean(company)} />}

          {canReadInvoices && (
            <>
              <div style={{ padding: '20px 24px 0' }}>
                <FaturamentoHero
                  loading={summaryQuery.isPending}
                  valor={summaryQuery.data?.faturamento_mes}
                  countMes={summaryQuery.data?.count_mes ?? 0}
                  countProcessing={summaryQuery.data?.count_processing ?? 0}
                  monthLabel={summaryQuery.data?.month_label}
                />
              </div>

              <div style={{ padding: '14px 24px 0', display: 'flex', gap: 10 }}>
                <Stat
                  label="Autorizadas"
                  value={
                    summaryQuery.data ? String(summaryQuery.data.count_mes).padStart(2, '0') : '—'
                  }
                  delta={company ? 'no mês' : undefined}
                  deltaType="pos"
                />
                <Stat
                  label="Em proc."
                  value={
                    summaryQuery.data
                      ? String(summaryQuery.data.count_processing).padStart(2, '0')
                      : '—'
                  }
                  delta={
                    summaryQuery.data && summaryQuery.data.count_processing > 0
                      ? 'aguardando'
                      : 'tudo ok'
                  }
                  deltaType={
                    summaryQuery.data && summaryQuery.data.count_processing > 0 ? 'warn' : 'neutral'
                  }
                />
                <Stat
                  label="Erros"
                  value={
                    summaryQuery.data ? String(summaryQuery.data.count_error).padStart(2, '0') : '—'
                  }
                  delta={
                    summaryQuery.data && summaryQuery.data.count_error > 0 ? 'revisar' : 'sem erros'
                  }
                  deltaType={
                    summaryQuery.data && summaryQuery.data.count_error > 0 ? 'neg' : 'neutral'
                  }
                />
              </div>

              <SectionHead
                label="Últimas notas"
                action={
                  <Link to={ROUTES.notas} style={{ color: 'inherit' }}>
                    Ver todas
                  </Link>
                }
              />
              <UltimasNotas
                loading={recentQuery.isPending}
                notas={recentQuery.data?.results ?? []}
                canIssue={canIssue}
              />
            </>
          )}

          <InstallPrompt />
          <IosInstallSheetAuto />

          <SectionHead label="Atalhos" />
          <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {shortcuts.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 16px',
                  background: 'var(--rx-surface)',
                  border: '0.5px solid var(--rx-border)',
                  borderRadius: 14,
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: 'var(--rx-brand-tint)',
                    color: 'var(--rx-brand)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon name={item.icon} size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--rx-text)',
                      letterSpacing: '-0.1px',
                    }}
                  >
                    {item.title}
                  </div>
                  <div
                    className="rx-mono"
                    style={{ marginTop: 2, fontSize: 11, color: 'var(--rx-text-3)' }}
                  >
                    {item.caption}
                  </div>
                </div>
                <Icon name="chevron" size={16} color="var(--rx-text-3)" />
              </Link>
            ))}
          </div>

          {company && (
            <div style={{ padding: '24px 24px 0' }}>
              <CompanySwitcher />
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

interface FaturamentoHeroProps {
  loading: boolean;
  valor: string | undefined;
  countMes: number;
  countProcessing: number;
  monthLabel: string | undefined;
}

function FaturamentoHero({
  loading,
  valor,
  countMes,
  countProcessing,
  monthLabel,
}: FaturamentoHeroProps): JSX.Element {
  const { integer, cents } = splitBrl(valor);
  const totalNotas = countMes + countProcessing;
  const progressPct =
    totalNotas > 0 ? Math.round((countMes / totalNotas) * 100) : countMes > 0 ? 100 : 0;

  return (
    <div className="rx-hero">
      <GridPattern
        size={180}
        step={10}
        stroke="#fff"
        strokeWidth={0.6}
        opacity={0.07}
        vertical={false}
        style={{ position: 'absolute', right: -40, top: -40 }}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            opacity: 0.7,
          }}
        >
          Faturamento · {monthLabel ? capitalize(monthLabel) : 'mês'}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span className="rx-mono">{progressPct}%</span>
          <span>autorizado</span>
        </span>
      </div>

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.6 }}>R$</span>
        {loading ? (
          <IonSkeletonText animated style={{ width: 140, height: 36, marginTop: 4 }} />
        ) : (
          <>
            <span
              className="rx-mono"
              style={{ fontSize: 36, fontWeight: 500, letterSpacing: '-1px' }}
            >
              {integer}
            </span>
            <span style={{ fontSize: 14, opacity: 0.6 }}>,{cents}</span>
          </>
        )}
      </div>

      <div
        style={{
          marginTop: 14,
          height: 4,
          background: 'rgba(255,255,255,0.15)',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${progressPct}%`,
            height: '100%',
            background: '#fff',
            borderRadius: 4,
            transition: 'width 300ms ease',
          }}
        />
      </div>

      <div
        style={{
          marginTop: 10,
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          opacity: 0.75,
        }}
      >
        <span className="rx-mono">{countMes.toString().padStart(2, '0')} autorizadas</span>
        <span className="rx-mono">
          {countProcessing.toString().padStart(2, '0')} em processamento
        </span>
      </div>
    </div>
  );
}

interface NoInvoiceAccessProps {
  hasCompany: boolean;
}

function NoInvoiceAccess({ hasCompany }: NoInvoiceAccessProps): JSX.Element {
  return (
    <div style={{ padding: '20px 24px 0' }}>
      <div className="rx-card" style={{ padding: '18px 16px', display: 'flex', gap: 12 }}>
        <Icon name="lock" size={18} color="var(--rx-text-3)" />
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--rx-text)' }}>
            {hasCompany ? 'Sem acesso às notas desta empresa' : 'Nenhuma empresa vinculada'}
          </div>
          <p style={{ marginTop: 4, fontSize: 13, color: 'var(--rx-text-2)' }}>
            {hasCompany
              ? 'Seu contador ainda não liberou o módulo de NFS-e para você nesta empresa.'
              : 'Peça ao seu contador para vincular uma empresa ao seu acesso.'}
          </p>
        </div>
      </div>
    </div>
  );
}

interface UltimasNotasProps {
  loading: boolean;
  notas: InvoiceListItem[];
  canIssue: boolean;
}

function UltimasNotas({ loading, notas, canIssue }: UltimasNotasProps): JSX.Element {
  if (loading) {
    return (
      <div style={{ padding: '0 24px' }}>
        <div className="rx-card rx-card-flush">
          {[0, 1, 2].map((idx) => (
            <div
              key={idx}
              style={{
                padding: '14px 16px',
                borderTop: idx === 0 ? 'none' : '0.5px solid var(--rx-border)',
              }}
            >
              <IonSkeletonText animated style={{ width: '60%', height: 14 }} />
              <IonSkeletonText animated style={{ width: '40%', height: 11, marginTop: 6 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (notas.length === 0) {
    return (
      <div style={{ padding: '0 24px' }}>
        <div className="rx-card" style={{ textAlign: 'center', padding: '28px 16px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--rx-text-3)',
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
            }}
          >
            Nenhuma nota ainda
          </div>
          <p
            style={{
              marginTop: 8,
              fontSize: 13,
              color: 'var(--rx-text-2)',
              maxWidth: '32ch',
              marginInline: 'auto',
            }}
          >
            {canIssue
              ? 'Emita a primeira NFSe em poucos passos.'
              : 'As notas emitidas para esta empresa aparecem aqui.'}
          </p>
          {canIssue && (
            <Link
              to={ROUTES.emitir}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 14,
                padding: '8px 12px',
                borderRadius: 999,
                background: 'var(--rx-brand-tint)',
                color: 'var(--rx-brand)',
                fontSize: 12,
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              <Icon name="flash" size={14} />
              Emitir agora
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 24px' }}>
      <div className="rx-card rx-card-flush">
        {notas.map((nota, idx) => (
          <NotaRow key={nota.id} nota={nota} first={idx === 0} />
        ))}
      </div>
    </div>
  );
}

interface NotaRowProps {
  nota: InvoiceListItem;
  first: boolean;
}

const SHORT_MONTH_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'short',
});

function NotaRow({ nota, first }: NotaRowProps): JSX.Element {
  const dateIso = nota.data_emissao ?? nota.created_at;
  const parts = SHORT_MONTH_FORMATTER.formatToParts(new Date(dateIso));
  const day = parts.find((p) => p.type === 'day')?.value ?? '—';
  const month = (parts.find((p) => p.type === 'month')?.value ?? '').replace('.', '').toUpperCase();
  const tone = STATUS_TONE[nota.status];
  const { integer, cents } = splitBrl(nota.valor_servico);

  return (
    <Link
      to={notaPath(nota.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderTop: first ? 'none' : '0.5px solid var(--rx-border)',
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          flexShrink: 0,
          background: 'var(--rx-surface-alt)',
          border: '0.5px solid var(--rx-border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          className="rx-mono"
          style={{ fontSize: 10, fontWeight: 600, color: 'var(--rx-text-3)' }}
        >
          {month}
        </div>
        <div
          className="rx-mono"
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--rx-text)',
            lineHeight: 1,
            letterSpacing: '-0.3px',
          }}
        >
          {day}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 6,
              background: tone.dotColor,
              flexShrink: 0,
            }}
            aria-hidden
          />
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--rx-text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nota.tomador_razao_social || nota.tomador_cpf_cnpj || 'Sem tomador'}
          </span>
        </div>
        <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className={`rx-tag ${tone.tagClass}`}>{nota.status_display}</span>
          {nota.numero != null && (
            <span className="rx-mono" style={{ fontSize: 10, color: 'var(--rx-text-3)' }}>
              Nº {nota.numero}
            </span>
          )}
        </div>
      </div>
      <div
        className="rx-mono"
        style={{
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: '-0.1px',
          color: 'var(--rx-text)',
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        R$ {integer}
        <span style={{ color: 'var(--rx-text-3)' }}>,{cents}</span>
      </div>
    </Link>
  );
}
